import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import SaveIcon from "@mui/icons-material/Save";
import Panel from "../../components/brand/Panel";

const money = (value) =>
  value == null
    ? "—"
    : new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(value);

const sectionLabel = (section) => {
  if (section.section_type === "estate") return "Estate Charge";
  return "Block Charge";
};

const sanitizeMoney = (value) => {
  const cleaned = String(value).replace(/[^0-9.]/g, "");
  const parts = cleaned.split(".");
  if (parts.length <= 1) return cleaned;
  return `${parts[0]}.${parts.slice(1).join("").slice(0, 2)}`;
};

const groupedLinesByCategory = (lines) => {
  const groups = new Map();
  lines.forEach((line) => {
    const category = line.category || "Uncategorised";
    if (!groups.has(category)) groups.set(category, []);
    groups.get(category).push(line);
  });
  return Array.from(groups, ([category, categoryLines]) => ({
    category,
    lines: categoryLines.sort(
      (a, b) =>
        (a.account_code || "").localeCompare(b.account_code || "") ||
        a.name.localeCompare(b.name)
    ),
  })).sort((a, b) => a.category.localeCompare(b.category));
};

export default function BudgetEditor({ budget, onSave, onStatus }) {
  const [draft, setDraft] = useState(budget);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    setDraft(budget);
  }, [budget]);

  const hasPreviousYear = (draft.sections || []).some((section) =>
    (section.lines || []).some((line) => line.previous_amount != null)
  );

  const updateLineAmount = (sectionId, lineId, amount) => {
    const cleaned = sanitizeMoney(amount);
    setDraft((prev) => ({
      ...prev,
      sections: prev.sections.map((section) =>
        section.id !== sectionId
          ? section
          : {
              ...section,
              lines: section.lines.map((line) =>
                line.id === lineId ? { ...line, amount: cleaned } : line
              ),
            }
      ),
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        row_version: draft.row_version,
        name: draft.name,
        sections: draft.sections.map((section, sIdx) => ({
          id: section.id,
          name: section.name,
          section_type: section.section_type,
          sort_order: sIdx,
          lines: section.lines.map((line, lIdx) => ({
            id: line.id,
            name: line.name,
            line_item_id: line.line_item_id,
            amount: line.amount === "" || line.amount == null ? 0 : Number(line.amount),
            previous_amount: line.previous_amount,
            sort_order: lIdx,
          })),
        })),
      });
    } finally {
      setSaving(false);
    }
  };

  const readOnly = draft.status === "published";

  if (!(draft.sections || []).length) {
    return (
      <Panel title={draft.name} noBodyPadding={false}>
        <Alert severity="warning">
          This budget has no sections. Save a budget format for this property, then create a new
          budget from that format.
        </Alert>
      </Panel>
    );
  }

  return (
    <Panel
      title={draft.name}
      noBodyPadding={false}
      right={
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Chip label={draft.status} size="small" />
          {!readOnly && (
            <Button size="small" variant="contained" onClick={handleSave} disabled={saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}>
              {saving ? "Saving…" : "Save budget"}
            </Button>
          )}
          {draft.status === "draft" && (
            <Button size="small" onClick={() => onStatus("approve")}>
              Approve
            </Button>
          )}
          {draft.status === "approved" && (
            <Button size="small" onClick={() => onStatus("publish")}>
              Publish
            </Button>
          )}
        </Box>
      }
    >
      {!readOnly && (
        <TextField
          fullWidth
          size="small"
          label="Budget name"
          value={draft.name || ""}
          onChange={(e) => setDraft((prev) => ({ ...prev, name: e.target.value }))}
          sx={{ mb: 2 }}
        />
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Total {money(draft.total)}
      </Typography>

      {(draft.sections || []).map((section) => (
        <Paper key={section.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1, gap: 2 }}>
            <Box>
              <Typography fontWeight={600}>{section.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {sectionLabel(section)}
              </Typography>
            </Box>
            <Typography variant="body2">Section total {money(section.total)}</Typography>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Budget line</TableCell>
                  {hasPreviousYear && <TableCell align="right">Previous year</TableCell>}
                  <TableCell align="right">This year</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupedLinesByCategory(section.lines || []).map((group) => (
                  <React.Fragment key={group.category}>
                    <TableRow>
                      <TableCell
                        colSpan={hasPreviousYear ? 4 : 3}
                        sx={{ bgcolor: "action.hover", fontWeight: 700 }}
                      >
                        {group.category}
                      </TableCell>
                    </TableRow>
                    {group.lines.map((line) => (
                      <TableRow key={line.id}>
                        <TableCell>{line.account_code || "—"}</TableCell>
                        <TableCell>{line.name}</TableCell>
                        {hasPreviousYear && (
                          <TableCell align="right">{money(line.previous_amount)}</TableCell>
                        )}
                        <TableCell align="right">
                          {readOnly ? (
                            money(line.amount)
                          ) : (
                            <TextField
                              size="small"
                              value={line.amount}
                              onChange={(e) =>
                                updateLineAmount(section.id, line.id, e.target.value)
                              }
                              inputProps={{
                                style: { textAlign: "right" },
                                inputMode: "decimal",
                              }}
                              InputProps={{
                                startAdornment: (
                                  <Box component="span" sx={{ mr: 0.5, color: "text.secondary" }}>
                                    £
                                  </Box>
                                ),
                              }}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {section.section_type !== "estate" &&
            (section.unit_allocations || []).length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                {(section.unit_allocations || []).length} unit(s) assigned — edit on the
                Apportionments tab
              </Typography>
            )}
        </Paper>
      ))}
      {!readOnly && (
        <Button variant="contained" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}>
          {saving ? "Saving…" : "Save budget"}
        </Button>
      )}
    </Panel>
  );
}
